import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Table, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ChangePasswordModal from '../components/ChangePasswordModal';

const formatUserGroup = (code) => {
  if (code === 0) return <Badge bg="primary">Master Administrator</Badge>;
  if (code === 1) return <Badge bg="info">Tenant Administrator</Badge>;
  if (code === 2) return <Badge bg="secondary">User</Badge>;
  return <Badge bg="light" text="dark">{String(code)}</Badge>;
};

const UserManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [parentGroupLabels, setParentGroupLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [passwordTarget, setPasswordTarget] = useState(null);

  // Direct-URL guard. Sidebar already hides this for non-admins, but if a
  // non-admin types the URL or has it bookmarked, send them to their own
  // profile page instead.
  useEffect(() => {
    if (user && user.userGroup !== 0) {
      navigate('/dashboard/profile', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user || user.userGroup !== 0) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get('/users/getAllUsers', {
          withCredentials: true
        });
        if (cancelled) return;
        const list = Array.isArray(response.data) ? response.data : [];
        // Stable sort: admins first, then alphabetical by lastName.
        list.sort((a, b) => {
          if (a.userGroup !== b.userGroup) return a.userGroup - b.userGroup;
          return (a.lastName || '').localeCompare(b.lastName || '');
        });
        setUsers(list);
        setLoading(false);

        // Resolve parent-group integer codes to labels in parallel, but only
        // once per unique code — typical accounts cluster in one or two groups,
        // so this collapses N requests down to ~1.
        const codes = [
          ...new Set(
            list
              .map((u) => (u.parentGroup ?? u.parentgroup_fk))
              .filter((v) => Number.isInteger(v))
          )
        ];
        if (codes.length === 0) return;

        const entries = await Promise.all(
          codes.map(async (code) => {
            try {
              const res = await axios.get(
                `/parentgroups/getParentGroupNameByStatusCode/${code}`,
                { withCredentials: true }
              );
              return [code, res.data?.statuslabel || `#${code}`];
            } catch (lookupErr) {
              console.warn(
                `Parent-group label lookup failed for ${code}:`,
                lookupErr.message
              );
              return [code, `#${code}`];
            }
          })
        );
        if (cancelled) return;
        setParentGroupLabels(Object.fromEntries(entries));
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load users:', err);
        setError(err.response?.data?.error || 'Failed to load users.');
        setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const renderParentGroupCell = (u) => {
    const code = u.parentGroup ?? u.parentgroup_fk;
    if (!Number.isInteger(code)) {
      return <span className="text-muted">&mdash;</span>;
    }
    const label = parentGroupLabels[code];
    if (label === undefined) {
      // Lookup still in flight.
      return <span className="text-muted">&hellip;</span>;
    }
    return label;
  };

  const renderLastLoginCell = (u) => {
    const v = u.last_login;
    if (v === undefined) return <span className="text-muted">&mdash;</span>;
    if (v === null) return <span className="text-muted fst-italic">Never</span>;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return <span className="text-muted">&mdash;</span>;
    const pad = (n) => String(n).padStart(2, '0');
    return (
      `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ` +
      `${pad(d.getHours())}:${pad(d.getMinutes())}`
    );
  };

  if (!user || user.userGroup !== 0) {
    // Effect above will redirect; render nothing in the meantime.
    return null;
  }

  return (
    <div className="content-container">
      <h3 className="mb-1">User Management</h3>
      <p className="text-muted small mb-0">
        Manage user accounts, roles, and credentials.
      </p>

      <Container fluid className="mt-4">
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" />
          </div>
        ) : users.length === 0 ? (
          <Alert variant="info">No users found.</Alert>
        ) : (
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Parent Group</th>
                  <th>Last Login</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id || u._id}>
                    <td>{u.firstName}</td>
                    <td>{u.lastName}</td>
                    <td>{u.email}</td>
                    <td>{formatUserGroup(u.userGroup)}</td>
                    <td>{renderParentGroupCell(u)}</td>
                    <td>{renderLastLoginCell(u)}</td>
                    <td className="text-end">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setPasswordTarget(u)}
                      >
                        Change Password
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Container>

      <ChangePasswordModal
        targetUser={passwordTarget}
        onClose={() => setPasswordTarget(null)}
      />
    </div>
  );
};

export default UserManagement;
